import React, { useEffect } from 'react';

import UploadFile from 'apps/auth/components/common/UploadFile/UploadFile';
import Checkbox from 'apps/auth/components/form/Checkbox';
import CountryDropBox from 'apps/auth/components/form/CountryDropBox';
import InputAccount from 'apps/auth/components/form/InputAccount';
import InputAutoCompleteAddress from 'apps/auth/components/form/InputAutoCompleteAddress';
import InputBik from 'apps/auth/components/form/InputBik';
import InputDate from 'apps/auth/components/form/InputDate';
import InputDropBox from 'apps/auth/components/form/InputDropBox';
import InputTextArea from 'apps/auth/components/form/InputTextArea';
import InputText from 'apps/auth/components/form-multi/InputText';
import { ACCOUNT_TO, MARKETING_OPT_IN, TO_BIC, TO_IBAN, TO_SORT_CODE } from 'apps/auth/constants/fieldsName';
import {
    ATTACHMENT,
    AUTOCOMPLETE_ADDRESS,
    AUTOCOMPLETE_POST_CODE,
    BANK_ACCOUNT,
    BIK,
    CHECK_BOX,
    CHECKBOX,
    COUNTRY,
    CVV,
    DATE,
    DEVICE_SCREEN_HEIGHT,
    DEVICE_SCREEN_WIDTH,
    DROP_BOX,
    EXP_DATE,
    FILE,
    HIDDEN,
    PAN,
    PHONE,
    PHONE_COUNTRY,
    PHONE_STRICT,
    SBP_DROP_BOX,
    TEXT,
    TEXTAREA,
    UPLOAD_DOC,
} from 'apps/auth/constants/FieldType';
import { USA } from 'apps/auth/constants/WellKnownCountries';
import { focusFirstInput } from 'apps/auth/helpers/utils';
import { useApp } from 'apps/auth/hooks/selectors';
import useAutoScrollOnFocus from 'apps/auth/hooks/useAutoScrollOnFocus';

import InputAutoCompletePostCode from './InputAutoCompletePostCode';
import InputHidden from './InputHidden';
import InputPhoneWithDropdown from './InputPhoneWithDropdown';
import UploadDocImg from './UploadDocImg';

const InputAutoDetect = (props) => {
    const { isShowModal } = useApp();

    useEffect(() => {
        if (!isShowModal && !props.firstInputNotFocused) {
            focusFirstInput();
        }
    }, [isShowModal]);

    useAutoScrollOnFocus();

    const detectFieldType = (field, type, country = {}) => {
        if (type === PHONE || type === PHONE_STRICT) {
            return (
                <InputPhoneWithDropdown
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    defaultCountry={props.defaultCountry}
                    iso2={props.defaultCountry}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    onChangeHandler={props.onChangeHandler}
                    onBlurHandler={props.onBlurHandler}
                    reportButtonClick={props.reportButtonClick}
                    isPhoneTypeStrict={type === PHONE_STRICT}
                    enablePhoneSuggestion
                    isNonEditableCountry
                />
            );
        } else if (type === TEXTAREA) {
            return (
                <InputTextArea
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    reportButtonClick={props.reportButtonClick}
                    isNewInput
                    alwaysHasHint={props.alwaysHasHint}
                />
            );
        } else if (type === PAN || type === CVV || type === EXP_DATE) {
            return false;
        } else if (type === CHECK_BOX) {
            return (
                <Checkbox
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    reportButtonClick={props.reportButtonClick}
                />
            );
        } else if (type === CHECKBOX && field?.name === MARKETING_OPT_IN) {
            return (
                <Checkbox
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    reportButtonClick={props.reportButtonClick}
                    checkBoxButtonCaption={props.checkBoxButtonCaption}
                    modalRef={props.modalRef}
                    size="secondary"
                    marketingOptIn
                />
            );
        } else if (type === COUNTRY && field.isReadOnly) {
            return (
                <CountryDropBox
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    selectHandler={props.selectHandler}
                    alwaysHasHint={props.alwaysHasHint}
                />
            );
        } else if (type === COUNTRY) {
            return (
                <CountryDropBox
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    customValue={props.customValue}
                    country={country}
                    clearFieldsOnCountrySelect={props.clearFieldsOnCountrySelect}
                    selectHandler={props.selectHandler}
                    alwaysHasHint={props.alwaysHasHint}
                />
            );
        } else if (type === DATE) {
            return (
                <InputDate
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    usaDateFormat={country.id === USA}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    reportButtonClick={props.reportButtonClick}
                    isNewInput
                />
            );
        } else if (type === BIK) {
            return (
                <InputBik
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    isClearErrorOnFocus={props.isClearErrorOnFocus}
                    onBlurHandler={props.onBlurHandler}
                    reportButtonClick={props.reportButtonClick}
                    isNewInput
                />
            );
        } else if (
            type === BANK_ACCOUNT ||
            field?.name === TO_SORT_CODE ||
            (type === TEXT && (field?.name === TO_IBAN || (field?.name === ACCOUNT_TO && props.formatAccountNumber)))
        ) {
            return (
                <InputAccount
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    onChangeHandler={props.onChangeHandler}
                    onBlurHandler={props.onBlurHandler}
                    reportButtonClick={props.reportButtonClick}
                    isNewInput
                    isFormattedInput
                />
            );
        } else if (type === DROP_BOX || type === SBP_DROP_BOX) {
            return (
                <InputDropBox
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    selectHandler={props.selectHandler}
                    clearFieldsOnCountrySelect={props.clearFieldsOnCountrySelect}
                    dropdownModalOverflowHidden={props.dropdownModalOverflowHidden}
                    hasCountryItemPanel={props.hasCountryItemPanel}
                />
            );
        } else if (type === PHONE_COUNTRY) {
            return (
                <CountryDropBox
                    readOnly={true}
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    alwaysHasHint={props.alwaysHasHint}
                />
            );
        } else if (type === AUTOCOMPLETE_ADDRESS) {
            return (
                <InputAutoCompleteAddress
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    countryCode={country.code}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    disabledAddressInput={!!props.disabledAddressInput}
                    alwaysHasHint={props.alwaysHasHint}
                />
            );
        } else if (type === AUTOCOMPLETE_POST_CODE) {
            return (
                <InputAutoCompletePostCode
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    isClearErrorOnFocus={props.isClearErrorOnFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    alwaysHasHint={props.alwaysHasHint}
                />
            );
        } else if (type === UPLOAD_DOC || type === ATTACHMENT) {
            return (
                <UploadDocImg
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                />
            );
        } else if (type === FILE) {
            return (
                <UploadFile
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                />
            );
        } else if (type === HIDDEN || type === DEVICE_SCREEN_HEIGHT || type === DEVICE_SCREEN_WIDTH) {
            return (
                <InputHidden
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    reportButtonClick={props.reportButtonClick}
                    isClearErrorOnFocus={props.isClearErrorOnFocus}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                />
            );
        } else if (type === TEXT && field?.name === TO_BIC) {
            return (
                <InputText
                    upperCase={true}
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    isClearErrorOnFocus={true}
                    onChange={props.onChange}
                    onChangeHandler={props.onChangeHandler}
                    onBlur={props.onBlur}
                    reportButtonClick={props.reportButtonClick}
                    onBlurHandler={props.onBlurHandler}
                    isNewInput
                    alwaysHasHint={props.alwaysHasHint}
                />
            );
        } else {
            return (
                <InputText
                    field={field}
                    hintButtonClick={props.hintButtonClick}
                    setMaxLength={props.setMaxLength}
                    formName={props.formName}
                    autoFocus={props.focus}
                    onFocus={props.onFocus}
                    isClearErrorOnFocus={true}
                    onChange={props.onChange}
                    onChangeHandler={props.onChangeHandler}
                    onBlur={props.onBlur}
                    reportButtonClick={props.reportButtonClick}
                    onBlurHandler={props.onBlurHandler}
                    isNewInput
                    alwaysHasHint={props.alwaysHasHint}
                />
            );
        }
    };

    const field = props.field;

    return detectFieldType(field, field.type, props.country);
};

export default InputAutoDetect;
